import * as React from 'react';
import { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuContentV2 from './sidebar/MenuContentV2';
import LanguageSwitcher from './shared/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SideMenuMobileProps {
  open: boolean | undefined;
  toggleDrawer: (newOpen: boolean) => () => void;
}

export default function SideMenuMobile({ open, toggleDrawer }: SideMenuMobileProps) {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  if (loading) {
    return <p>{t('common:status.loading')}</p>;
  }

  const userId = user?.uid;
  const userEmail = user?.email || t('dashboard:sidebar.unknownEmail');
  const userName = user?.displayName || t('dashboard:sidebar.unknownName');
  const userPhoto = user?.photoURL || "/static/images/avatar/7.jpg"; // Photo par défaut si non disponible

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setLogoutDialogOpen(false);
      toggleDrawer(false)();
      navigate('/landing');
    } catch (error) {
      // Error("Erreur de déconnexion :", error);
      setLogoutDialogOpen(false);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: 'none',
          // backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack
        sx={{
          // Largeur responsive pour mieux supporter les petits écrans
          width: { xs: '85vw', sm: '75vw' },
          maxWidth: { xs: '320px', sm: '360px' },
          height: '100%',
        }}
      >
        {/* Logo Foot in the Door */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => {
            navigate('/dashboard/match-emploi');
            toggleDrawer(false)();
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: '"DM Serif Display", Georgia, serif',
              fontWeight: 400,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              color: theme.palette.mode === 'dark' ? '#FAFAF9' : '#1C1917',
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
              textAlign: 'center',
            }}
          >
            foot in the{' '}
            <Box
              component="span"
              sx={{
                color: '#22C55E', // Primary green accent
              }}
            >
              door
            </Box>
          </Typography>
        </Box>

        {/* 🔥 En-tête avec Avatar et Nom */}
        <Stack direction="row" sx={{ p: 2, pb: 0, gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: 'center', flexGrow: 1, p: 1 }}
          >
            <Avatar
              alt={userName}
              src={userPhoto}
              sx={{ width: 40, height: 40 }}
            />
            <Typography
              component="p"
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '180px',
              }}
            >
              {userName}
            </Typography>
          </Stack>
          {/* <MenuButton showBadge>
            <NotificationsRoundedIcon />
          </MenuButton> */}
        </Stack>

        <Divider />

        {/* 🔥 Contenu du menu */}
        <Stack sx={{ flexGrow: 1 }}>
          <MenuContentV2 />
          <Divider />
        </Stack>
        {/*
        <CardAlert /> */}

        {/* Language switcher */}
        <Stack sx={{ px: 2, py: 1 }}>
          <LanguageSwitcher />
        </Stack>

        {/* 🔥 Bouton de Déconnexion */}
        <Stack sx={{ p: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LogoutRoundedIcon />}
            onClick={handleLogoutClick}
          >
            {t('common:nav.logout')}
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          {t('common:confirm.logoutTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {t('common:confirm.logoutMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleLogoutConfirm} color="primary" variant="contained" autoFocus>
            {t('common:nav.logout')}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
